import type { Doc } from "building-types/doc";
import type { Label } from "building-types/entities/labelTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { LabelableEntityHandle } from "building-types/handles/labelableEntityHandle";
import type { RWTable, Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import objectForEach from "common/utils/objectForEach";
import objectKeyFilter from "common/utils/objectKeyFilter";
import { isBuiltInLabel } from "../utils/builtInLabelUtils";
import { docEntityHandleIsLabelable } from "./docEntityHandleTools";
import { docGetAnnotationLabels } from "./docGetAnnotation";
import { docGetPropAttributes, docGetPropLabels } from "./docGetProps";
import { docGetTableEntity } from "./docGetTableEntity";

export function docGetLabelTable(doc: Doc, handle: DocEntityHandle, includeBuiltIn = true): Table<boolean> {
  if (!docEntityHandleIsLabelable(handle)) {
    return {};
  }

  let labels: RWTable<boolean> = {};

  switch (handle.type) {
    case "VIEW_TEMPLATE_OVERRIDE":
    case "SHEET":
    case "CEILING":
    case "CONTENT_TEMPLATE":
    case "CONTENT_TEMPLATE_INSTANCE":
    case "COMPONENT_INSTANCE":
    case "ROOM":
    case "ROOF":
    case "FLOOR":
    case "FLOOR_SYSTEM":
    case "FOUNDATION":
    case "STAIR_SYSTEM":
    case "RAILING":
    case "WALL_PLANE_ASSEMBLY":
    case "ELECTRICAL_DEVICE":
      labels = docGetTableEntity(doc, handle)?.labels ?? {};
      break;
    case "PROP_INSTANCE":
      const propInstance = docGetTableEntity(doc, handle);

      if (!propInstance) {
        return {};
      }

      labels = docGetPropLabels(doc, propInstance);
      break;
    case "ANNOTATION":
      const annotation = docGetTableEntity(doc, handle);

      if (!annotation) {
        return {};
      }

      labels = docGetAnnotationLabels(doc, annotation);
      break;
    case "PROP":
      labels = docGetPropAttributes(doc, handle)?.labels ?? {};
      break;
    case "PLAN_VIEW":
    case "ELEVATION_VIEW":
    case "SECTION_VIEW":
      labels = docGetTableEntity(doc, handle)?.viewFilter?.labels ?? {};
      break;
    default:
      return assertUnreachable(handle);
  }

  if (!includeBuiltIn) {
    return objectKeyFilter(labels, (label) => !isBuiltInLabel(label));
  }

  return labels;
}

export function docGetCommonLabelTableForHandles(
  doc: Doc,
  handles: LabelableEntityHandle[],
  includeBuiltIn = true,
): Table<boolean> {
  const entityLabels: { [handleId: string]: Table<boolean> } = {};
  const commonLabels: RWTable<boolean> = {};

  for (const handle of handles) {
    let entityId: string | undefined;

    switch (handle.type) {
      case "PROP":
        entityId = handle.id;
        break;
      default:
        entityId = handle.id;
    }

    if (!entityId) {
      continue;
    }

    const labels = docGetLabelTable(doc, handle, includeBuiltIn);
    entityLabels[entityId] = labels;
  }

  for (const entityId in entityLabels) {
    const labels = entityLabels[entityId];

    for (const labelId in labels) {
      if (Object.values(entityLabels).every((labels) => labels[labelId])) {
        commonLabels[labelId] = true;
      }
    }
  }

  return commonLabels;
}

export function entityForEachLabel(
  doc: Doc,
  handle: LabelableEntityHandle,
  callback: (label: Label) => void,
  includeBuiltInLabels = true,
): void {
  objectForEach(docGetLabelTable(doc, handle, includeBuiltInLabels), (_, labelId) => callback(doc.labels[labelId]));
}
